/* ==============================================================
   CUSTOM CANVAS PRINT BUILDER
   Section: sections/custom-canvas-builder.liquid
   Behavior:
     - state machine for the configurator
     - live preview (image + filter + frame + edge + aspect ratio)
     - image upload via File API → object URL preview + FormData to cart
     - Shopify AJAX cart: /cart/add.js supports files inside FormData
     - service add-ons added as separate line items (if merchant configured products)
   ============================================================== */

(function () {
  'use strict';

  const FILTER_MAP = {
    none: 'none',
    black_white: 'grayscale(1) contrast(1.05)',
    sepia: 'sepia(0.85) saturate(1.2)',
    vintage: 'sepia(0.35) contrast(0.95) saturate(0.85) hue-rotate(-8deg) brightness(1.02)',
    lomo: 'saturate(1.6) contrast(1.15) brightness(0.95)'
  };

  /* ENGLISH SIZE LABELS — hardcoded here because Shopify Markets' French locale
     auto-converts inches to cm when the label comes from Liquid (even in a data
     attribute). JS asset files are NOT touched by that translation layer, so this
     keeps all labels in a single English format for every market/language.
     Each entry is {tier, dim}; tier is the Small/Medium/Large/XL category label
     the client requested, dim is the raw dimension string. Keyed by `W,H`. */
  const SIZE_LABELS_IN = {
    // Smallest (OOS placeholders — client-added 2026-10-19)
    '8,12':  { tier: 'Smallest',    dim: '8 × 12 in'  },
    '12,8':  { tier: 'Smallest',    dim: '12 × 8 in'  },
    '8,8':   { tier: 'Smallest',    dim: '8 × 8 in'   },
    // Landscape (7)
    '18,12': { tier: 'Small',       dim: '18 × 12 in' },
    '24,16': { tier: 'Medium',      dim: '24 × 16 in' },
    '27,18': { tier: 'Medium',      dim: '27 × 18 in' },
    '30,20': { tier: 'Large',       dim: '30 × 20 in' },
    '36,24': { tier: 'Large',       dim: '36 × 24 in' },
    '45,30': { tier: 'Extra Large', dim: '45 × 30 in' },
    '48,32': { tier: 'Extra Large', dim: '48 × 32 in' },
    // Portrait (7) — mirror of landscape
    '12,18': { tier: 'Small',       dim: '12 × 18 in' },
    '16,24': { tier: 'Medium',      dim: '16 × 24 in' },
    '18,27': { tier: 'Medium',      dim: '18 × 27 in' },
    '20,27': { tier: 'Medium',      dim: '20 × 27 in' },
    '20,30': { tier: 'Large',       dim: '20 × 30 in' },
    '24,36': { tier: 'Large',       dim: '24 × 36 in' },
    '26,36': { tier: 'Large',       dim: '26 × 36 in' },
    '30,45': { tier: 'Extra Large', dim: '30 × 45 in' },
    '32,48': { tier: 'Extra Large', dim: '32 × 48 in' },
    '33,45': { tier: 'Extra Large', dim: '33 × 45 in' },
    // Square (7) — client wants 12,16,20,24,30,36,40
    '12,12': { tier: 'Small',       dim: '12 × 12 in' },
    '16,16': { tier: 'Medium',      dim: '16 × 16 in' },
    '20,20': { tier: 'Medium',      dim: '20 × 20 in' },
    '24,24': { tier: 'Large',       dim: '24 × 24 in' },
    '30,30': { tier: 'Large',       dim: '30 × 30 in' },
    '36,36': { tier: 'Extra Large', dim: '36 × 36 in' },
    '40,40': { tier: 'Extra Large', dim: '40 × 40 in' },
    // Panoramic (7) — client's exact spec: 18×9, 24×12, 30×15, 36×18, 40×20, 48×24, 60×30
    '18,9':  { tier: 'Small',       dim: '18 × 9 in' },
    '24,12': { tier: 'Medium',      dim: '24 × 12 in' },
    '30,15': { tier: 'Medium',      dim: '30 × 15 in' },
    '36,18': { tier: 'Large',       dim: '36 × 18 in' },
    '40,20': { tier: 'Large',       dim: '40 × 20 in' },
    '48,24': { tier: 'Extra Large', dim: '48 × 24 in' },
    '60,30': { tier: 'Extra Large', dim: '60 × 30 in' },
    // Legacy variant titles still mapped so existing Shopify variants keep working
    '24,18': { tier: '',            dim: '24 × 18 in' },
    '18,24': { tier: '',            dim: '18 × 24 in' },
    '44,44': { tier: '',            dim: '44 × 44 in' },
    '24,8':  { tier: '',            dim: '24 × 8 in'  },
    '30,10': { tier: '',            dim: '30 × 10 in' },
    '36,12': { tier: '',            dim: '36 × 12 in' },
    '42,14': { tier: '',            dim: '42 × 14 in' },
    '48,16': { tier: '',            dim: '48 × 16 in' },
    '54,18': { tier: '',            dim: '54 × 18 in' }
  };

  /* VARIANT IDs — hardcoded for the SAME reason: in French locale, Liquid's
     `block.settings.label` is auto-translated to French/metric, so the variant
     matching against `v.title` (which stays English) fails and every size falls
     back to the default variant. This map resolves the correct Shopify variant
     for each W × H regardless of locale.

     Live product variants (as of 2026-11-03) and their exact prices in the
     Shopify admin:
       Landscape:  18×12/$79.95 · 24×16/$89.95 · 27×20/$99.95 · 30×20/$119.95
                   36×26/$139.95 · 45×33/$169.95 · 48×32/$199.95
       Portrait:   12×18/$79.95 · 16×24/$89.95 · 18×27/$99.95 · 20×30/$119.95
                   24×36/$139.95 · 30×45/$169.95 · 32×48/$199.95
       Square:     12×12/$79.95 · 16×16/$89.95 · 20×20/$99.95 · 24×24/$119.95
                   30×30/$139.95 · 36×36/$169.95 · 44×44/$219.95
       Panoramic:  24×12/$89.95 · 30×15/$99.95 · 36×18/$119.95 · 40×20/$139.95
                   48×24/$169.95 · 60×30/$219.95

     Keys below map the shopper's SELECTED W,H to the Shopify variant that will
     be charged. Where an exact-title variant exists, the cart & checkout show
     the shopper's selection verbatim. Where no exact variant exists (client's
     bespoke 27×18 / 36×24 / 45×30 Landscape, 20×27 / 26×36 / 33×45 Portrait,
     40×40 Square) we route to the closest-price variant so the PRICE charged
     matches the page; the Shopify checkout then shows that variant's own
     title. To make checkout display the exact shopper selection too, the
     admin must RENAME those variants (see client handoff notes). */
  const SIZE_VARIANTS = {
    // Smallest (OOS; safety-net route only — click is blocked in UI)
    '8,12':  67669498986795, // → 12×18 Portrait variant
    '12,8':  67669479227691, // → 18×12 Landscape variant
    '8,8':   67669499216171, // → 12×12 Square variant

    // LANDSCAPE — exact matches where available
    '18,12': 67669479227691, // ✓ exact "18 × 12 in"
    '24,16': 67669498790187, // ✓ exact "24 × 16 in"
    '27,18': 67669498822955, // ⚠ no exact — routes to "27 × 20 in" variant ($99.95). ADMIN RENAME → "27 × 18 in"
    '27,20': 67669498822955, // ✓ exact "27 × 20 in" (safety map)
    '30,20': 67669498855723, // ✓ exact "30 × 20 in"
    '36,24': 67669498888491, // ⚠ no exact — routes to "36 × 26 in" variant ($139.95). ADMIN RENAME → "36 × 24 in"
    '36,26': 67669498888491, // ✓ exact "36 × 26 in"
    '45,30': 67669498921259, // ⚠ no exact — routes to "45 × 33 in" variant ($169.95). ADMIN RENAME → "45 × 30 in"
    '45,33': 67669498921259, // ✓ exact "45 × 33 in"
    '48,32': 67669498954027, // ✓ exact "48 × 32 in"

    // PORTRAIT — exact matches where available
    '12,18': 67669498986795, // ✓ exact "12 × 18 in"
    '16,24': 67669499019563, // ✓ exact "16 × 24 in"
    '18,27': 67669499052331, // ✓ exact "18 × 27 in"
    '20,27': 67669499052331, // ⚠ no exact — routes to "18 × 27 in" variant ($99.95). ADMIN RENAME → "20 × 27 in"
    '20,30': 67669499085099, // ✓ exact "20 × 30 in"
    '24,36': 67669499117867, // ✓ exact "24 × 36 in"
    '26,36': 67669499117867, // ⚠ no exact — routes to "24 × 36 in" variant ($139.95). ADMIN RENAME → "26 × 36 in"
    '30,45': 67669499150635, // ✓ exact "30 × 45 in"
    '32,48': 67669499183403, // ✓ exact "32 × 48 in"
    '33,45': 67669499150635, // ⚠ no exact — routes to "30 × 45 in" variant ($169.95). ADMIN RENAME → "33 × 45 in"

    // SQUARE — exact matches
    '12,12': 67669499216171,
    '16,16': 67669499248939,
    '20,20': 67669499281707,
    '24,24': 67669499314475,
    '30,30': 67669499347243,
    '36,36': 67669499380011,
    '44,44': 67669499412779,
    '40,40': 67669499412779, // ⚠ no exact — routes to "44 × 44 in" variant ($219.95). ADMIN RENAME → "40 × 40 in" or fix block dims

    // PANORAMIC — admin RENAMED all of these to match the client's requested
    // dimensions. Earlier mapping was shifted-by-one (each key pointed to the
    // NEXT size's variant), which was the French-cart bug. Now every panoramic
    // selection maps to its own exact-title variant.
    '18,9':  67669499445547, // OOS fallback → 24×12 variant
    '24,12': 67669499445547, // ✓ exact "24 × 12 in"
    '30,15': 67669499478315, // ✓ exact "30 × 15 in"
    '36,18': 67669499511083, // ✓ exact "36 × 18 in"
    '40,20': 67669499543851, // ✓ exact "40 × 20 in"
    '48,24': 67669499576619, // ✓ exact "48 × 24 in"
    '60,30': 67669499609387  // ✓ exact "60 × 30 in"
  };

  const MONEY_FORMATS = {
    default: '${{amount}}'
  };

  function formatMoney(cents, fmt) {
    const format = fmt || (window.Shopify && Shopify.currency && Shopify.money_format) || MONEY_FORMATS.default;
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount(?:_[^}]+)?\s*\}\}/g, value);
  }

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  class CanvasBuilder {
    constructor(root) {
      this.root = root;
      this.data = this.readConfig();
      // pick default finish from blocks
      let defaultFinish = { mode: 'mirror', label: 'Mirror Image', defaultColor: '#ffffff' };
      if (this.data.finishes && this.data.finishes.length) {
        const flaggedFinish = this.data.finishes.find(f => f.is_default) || this.data.finishes[0];
        defaultFinish = flaggedFinish;
      }
      this.state = {
        orientation: this.data.defaultOrientation || 'square',
        size: null, // {label, price_cents, compare_at_cents, variantId, orientation}
        isCustomSize: false,
        rotY: 18,    // current Y rotation (degrees) — tilted so LEFT side is visible (canvaspeople-style)
        rotX: 2,     // current X rotation (degrees)
        edge: this.data.defaultEdge || 'photo_wrap',
        frame: this.data.defaultFrame || 'none',
        filter: this.data.defaultFilter || 'none',
        service: 'none',
        serviceUpchargeCents: 0,
        serviceLabel: 'None',
        hanger: 'none',
        hangerLabel: 'None',
        hangerUpchargeCents: 0,
        hangerVariantId: null,
        finish: defaultFinish.mode,
        finishLabel: defaultFinish.label,
        finishColor: defaultFinish.defaultColor || '#ffffff',
        notes: '',
        imageFile: null,
        imageURL: null,
        imageName: ''
      };

      // pick default size: explicit is_default flag wins; else first size matching default orientation; else first size.
      // Skip out-of-stock sizes in the fallback paths so the shopper never lands on an unselectable pill.
      const flagged = this.data.sizes.find(s => s.is_default && !s.out_of_stock);
      if (flagged) {
        this.state.size = flagged;
        this.state.orientation = flagged.orientation || this.state.orientation;
      } else {
        const firstMatching = this.data.sizes.find(s => s.orientation === this.state.orientation && !s.out_of_stock)
          || this.data.sizes.find(s => !s.out_of_stock)
          || this.data.sizes[0];
        if (firstMatching) this.state.size = firstMatching;
      }

      this.cacheEls();
      this.bindEvents();
      this.render();
    }

    readConfig() {
      const script = this.root.querySelector('script[data-ccb-config]');
      if (!script) return { sizes: [], services: [] };
      try {
        const parsed = JSON.parse(script.textContent);
        // Overwrite each size's label AND variantId from the hardcoded JS maps.
        // Shopify Markets' French locale translates both the label (→ cm) and the
        // variant.title string Liquid uses for matching (→ "18 po × 12 po"), which
        // breaks the Liquid variant lookup and makes every size fall back to the
        // default $49.95 variant. Hardcoding here guarantees the right variant +
        // the right label in every locale.
        if (parsed && parsed.sizes) {
          parsed.sizes.forEach(sz => {
            const key = sz.width + ',' + sz.height;
            const entry = SIZE_LABELS_IN[key];
            if (entry) {
              sz.dim = entry.dim;
              // Prefer the Liquid-provided tier; fall back to the JS map's tier
              if (!sz.tier) sz.tier = entry.tier || '';
              // Full label (used in cart line items + summary): "Medium 16 × 24 in"
              sz.label = (sz.tier ? sz.tier + ' ' : '') + entry.dim;
            }
            if (SIZE_VARIANTS[key]) sz.variantId = SIZE_VARIANTS[key];
          });
        }
        return parsed;
      } catch (e) {
        console.error('[CanvasBuilder] Failed to parse config JSON', e);
        return { sizes: [], services: [] };
      }
    }

    cacheEls() {
      this.els = {
        title: $('[data-ccb-title]', this.root),
        priceCompare: $('[data-ccb-price-compare]', this.root),
        priceSale: $('[data-ccb-price-sale]', this.root),
        priceFinalCompare: $('[data-ccb-price-final-compare]', this.root),
        priceFinal: $('[data-ccb-price-final]', this.root),
        cta: $$('[data-ccb-cta]', this.root),
        fileInput: $('[data-ccb-file]', this.root),
        canvas: $('[data-ccb-canvas]', this.root),
        canvasImg: $('[data-ccb-canvas-img]', this.root),
        canvasBlurBg: $('[data-ccb-canvas-blur]', this.root),
        canvasPlaceholder: $('[data-ccb-canvas-placeholder]', this.root),
        frame: $('[data-ccb-frame]', this.root),
        panelValues: {
          product: $('[data-ccb-panel-value="product"]', this.root),
          shape: $('[data-ccb-panel-value="shape"]', this.root),
          sizes: $('[data-ccb-panel-value="sizes"]', this.root),
          edges: $('[data-ccb-panel-value="edges"]', this.root),
          frames: $('[data-ccb-panel-value="frames"]', this.root),
          filters: $('[data-ccb-panel-value="filters"]', this.root),
          services: $('[data-ccb-panel-value="services"]', this.root),
          hanger: $('[data-ccb-panel-value="hanger"]', this.root),
          finish: $('[data-ccb-panel-value="finish"]', this.root)
        },
        summaryList: $('[data-ccb-summary]', this.root),
        toast: $('[data-ccb-toast]', this.root),
        notesTextarea: $('[data-ccb-notes]', this.root),
        customW: $('[data-ccb-custom-w]', this.root),
        customH: $('[data-ccb-custom-h]', this.root),
        customApply: $('[data-ccb-custom-apply]', this.root),
        finishColorRow: $('[data-ccb-finish-color-row]', this.root),
        finishColorInput: $('[data-ccb-finish-color]', this.root),
        rotateBtn: $('[data-ccb-360]', this.root),
        cube: $('[data-ccb-cube]', this.root),
        sideImages: $$('[data-ccb-side]', this.root)
      };
    }

    bindEvents() {
      // Panels toggle
      $$('[data-ccb-panel-toggle]', this.root).forEach(btn => {
        btn.addEventListener('click', () => {
          const panel = btn.closest('.ccb-panel');
          const open = panel.getAttribute('data-open') !== 'false';
          panel.setAttribute('data-open', open ? 'false' : 'true');
        });
      });

      // Shape pills / orientation tabs
      $$('[data-ccb-orientation]', this.root).forEach(tab => {
        tab.addEventListener('click', () => this.setOrientation(tab.getAttribute('data-ccb-orientation')));
      });

      // Custom size
      if (this.els.customApply) {
        this.els.customApply.addEventListener('click', () => this.applyCustomSize());
      }
      [this.els.customW, this.els.customH].forEach(sel => {
        if (sel) sel.addEventListener('change', () => {
          if (this.els.customW?.value && this.els.customH?.value) this.applyCustomSize();
        });
      });

      // Hanger set
      $$('[data-ccb-hanger]', this.root).forEach(el => {
        el.addEventListener('click', () => {
          this.setHanger({
            id: el.getAttribute('data-ccb-hanger'),
            label: el.getAttribute('data-ccb-hanger-label') || '',
            upchargeCents: parseInt(el.getAttribute('data-ccb-hanger-price') || '0', 10),
            variantId: el.getAttribute('data-ccb-hanger-variant') || null
          });
        });
      });

      // Finish (Mirror / Border Color)
      $$('[data-ccb-finish]', this.root).forEach(el => {
        el.addEventListener('click', () => {
          this.setFinish(
            el.getAttribute('data-ccb-finish'),
            el.getAttribute('data-ccb-finish-label') || ''
          );
        });
      });
      if (this.els.finishColorInput) {
        this.els.finishColorInput.addEventListener('input', (e) => {
          this.state.finishColor = e.target.value;
          this.render();
        });
      }

      // Sizes — inject HARDCODED English tier + dimension spans so Shopify Markets'
      // French auto-translation can't convert "16 × 24 in" → "40,64 × 60,96 cm".
      // The data attribute + inner text get translated by Shopify; this JS doesn't.
      $$('[data-ccb-size]', this.root).forEach(btn => {
        const idx = parseInt(btn.getAttribute('data-ccb-size'), 10);
        const size = this.data.sizes[idx];
        if (size) {
          const tier = size.tier || '';
          const dim  = size.dim  || size.label || '';
          btn.innerHTML =
            (tier ? '<span class="ccb-size-pill__tier">' + tier + '</span>' : '') +
            '<span class="ccb-size-pill__dim">' + dim + '</span>';
          // Add the Recommended ribbon badge if flagged
          if (size.recommended) {
            btn.classList.add('is-recommended');
            if (!btn.querySelector('.ccb-size-pill__reco')) {
              const reco = document.createElement('span');
              reco.className = 'ccb-size-pill__reco';
              reco.textContent = 'Recommended';
              btn.appendChild(reco);
            }
          }
          // Out-of-stock pill: dim it, add "Out of Stock" chip, mark aria-disabled.
          // The click still fires so setSize can show a toast; CSS blocks selection visually.
          if (size.out_of_stock) {
            btn.classList.add('is-oos');
            btn.setAttribute('aria-disabled', 'true');
            btn.setAttribute('data-ccb-oos', 'true');
            if (!btn.querySelector('.ccb-size-pill__oos')) {
              const oos = document.createElement('span');
              oos.className = 'ccb-size-pill__oos';
              oos.textContent = 'Out of Stock';
              btn.appendChild(oos);
            }
          }
        }
        btn.addEventListener('click', () => {
          if (size) this.setSize(size);
        });
      });

      // Edges / Frames / Filters (each has data-ccb-set + data-ccb-value)
      $$('[data-ccb-set]', this.root).forEach(el => {
        el.addEventListener('click', () => {
          const kind = el.getAttribute('data-ccb-set');
          const value = el.getAttribute('data-ccb-value');
          const label = el.getAttribute('data-ccb-label') || value;
          this.setOption(kind, value, label);
        });
      });

      // Services
      $$('[data-ccb-service]', this.root).forEach(el => {
        el.addEventListener('click', () => {
          this.setService({
            id: el.getAttribute('data-ccb-service'),
            label: el.getAttribute('data-ccb-service-label') || '',
            upchargeCents: parseInt(el.getAttribute('data-ccb-service-price') || '0', 10),
            productId: el.getAttribute('data-ccb-service-product') || null,
            variantId: el.getAttribute('data-ccb-service-variant') || null
          });
        });
      });

      // Notes
      if (this.els.notesTextarea) {
        this.els.notesTextarea.addEventListener('input', (e) => {
          this.state.notes = e.target.value;
        });
      }

      // File input
      if (this.els.fileInput) {
        this.els.fileInput.addEventListener('change', (e) => {
          const file = e.target.files && e.target.files[0];
          if (file) this.handleFile(file);
        });
      }

      // CTA click
      this.els.cta.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleCTA(btn);
        });
      });

      // ===== 3D cube interaction: drag to rotate + 360° auto-spin =====
      this.setupCubeInteraction();
    }

    setupCubeInteraction() {
      const frame = this.els.frame;
      const cube = this.els.cube;
      if (!frame || !cube) return;

      // Initial dimensions + rotation
      this.updateCubeDimensions();
      this.applyRotation(true); // skip transition on first paint

      // Recalculate cube dims when the frame size changes (orientation / size / viewport)
      if (window.ResizeObserver) {
        this._resizeObserver = new ResizeObserver(() => this.updateCubeDimensions());
        this._resizeObserver.observe(frame);
      } else {
        window.addEventListener('resize', () => this.updateCubeDimensions());
      }

      // Pointer drag to rotate (works for mouse + touch + pen)
      let dragging = false;
      let startX = 0, startY = 0;
      let startRotY = 0, startRotX = 0;
      let activePointerId = null;

      const onPointerDown = (e) => {
        // Ignore clicks on the 360° button or inside the hint — only drag on the canvas itself
        if (e.target.closest('[data-ccb-360]')) return;
        dragging = true;
        activePointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        startRotY = this.state.rotY;
        startRotX = this.state.rotX;
        frame.classList.add('is-dragging');
        cube.classList.add('no-transition');
        // Stop any running auto-rotation
        this._autoRotating = false;
        try { frame.setPointerCapture(e.pointerId); } catch (_) {}
        e.preventDefault();
      };

      const onPointerMove = (e) => {
        if (!dragging || e.pointerId !== activePointerId) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        // Clamp rotation so the user can inspect the sides but NEVER reach the back of the canvas.
        this.state.rotY = Math.max(-75, Math.min(75, startRotY + dx * 0.5));
        this.state.rotX = Math.max(-20, Math.min(20, startRotX - dy * 0.3));
        this.applyRotation(true);
        e.preventDefault();
      };

      const onPointerUp = (e) => {
        if (!dragging) return;
        dragging = false;
        activePointerId = null;
        frame.classList.remove('is-dragging');
        // Re-enable transition for the snap-back / auto-rotate smoothness
        setTimeout(() => cube.classList.remove('no-transition'), 20);
      };

      frame.addEventListener('pointerdown', onPointerDown);
      frame.addEventListener('pointermove', onPointerMove);
      frame.addEventListener('pointerup', onPointerUp);
      frame.addEventListener('pointercancel', onPointerUp);
      frame.addEventListener('pointerleave', (e) => { if (dragging) onPointerUp(e); });

      // 360° button → auto-rotate a full turn (unless user is dragging)
      if (this.els.rotateBtn) {
        this.els.rotateBtn.addEventListener('click', () => this.autoRotate360());
      }
    }

    updateCubeDimensions() {
      if (!this.els.cube || !this.els.frame) return;
      const rect = this.els.frame.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      // Depth scales lightly with canvas size — kept subtle (realistic 1.5" wrap, not a thick box).
      const shortest = Math.min(rect.width, rect.height);
      const depth = Math.max(12, Math.min(18, shortest * 0.045));
      this.els.cube.style.setProperty('--ccb-w', rect.width + 'px');
      this.els.cube.style.setProperty('--ccb-h', rect.height + 'px');
      this.els.cube.style.setProperty('--ccb-d', depth.toFixed(1) + 'px');
    }

    applyRotation(immediate) {
      if (!this.els.cube) return;
      // `immediate` means skip the smooth .55s transition — used during drag + after auto-spin cleanup
      if (immediate) {
        this.els.cube.classList.add('no-transition');
      } else {
        this.els.cube.classList.remove('no-transition');
      }
      this.els.cube.style.setProperty('--rot-y', this.state.rotY + 'deg');
      this.els.cube.style.setProperty('--rot-x', this.state.rotX + 'deg');
    }

    autoRotate360() {
      // Client requirement: show both canvas sides WITHOUT ever exposing the back face.
      // We oscillate: current → +70° (reveal left side) → -70° (reveal right side) → default tilt.
      if (this._autoRotating) return;
      this._autoRotating = true;
      const startX = this.state.rotX;
      const defaultY = 18;
      const stops = [
        { y: 70,  dur: 1100 },
        { y: -70, dur: 1600 },
        { y: defaultY, dur: 1100 }
      ];
      this.els.cube.classList.remove('no-transition');

      const runStop = (i) => {
        if (!this._autoRotating || i >= stops.length) {
          this._autoRotating = false;
          this.els.cube.style.transition = '';
          this.els.cube.style.transform = '';
          this.state.rotY = defaultY;
          this.applyRotation(true);
          return;
        }
        const s = stops[i];
        this.els.cube.style.transition = 'transform ' + s.dur + 'ms cubic-bezier(.4,0,.2,1)';
        // eslint-disable-next-line no-unused-expressions
        this.els.cube.offsetWidth;
        this.els.cube.style.transform = 'rotateX(' + startX + 'deg) rotateY(' + s.y + 'deg)';
        const onEnd = (e) => {
          if (e.target !== this.els.cube) return;
          this.els.cube.removeEventListener('transitionend', onEnd);
          runStop(i + 1);
        };
        this.els.cube.addEventListener('transitionend', onEnd);
        setTimeout(() => {
          this.els.cube.removeEventListener('transitionend', onEnd);
          runStop(i + 1);
        }, s.dur + 150);
      };

      // Set starting point then begin
      this.els.cube.style.transition = 'none';
      this.els.cube.style.transform = 'rotateX(' + startX + 'deg) rotateY(' + this.state.rotY + 'deg)';
      // eslint-disable-next-line no-unused-expressions
      this.els.cube.offsetWidth;
      runStop(0);
    }

    setOrientation(orientation) {
      this.state.orientation = orientation;
      // pick first IN-STOCK size matching this orientation so the shopper never
      // auto-lands on an OOS pill when switching shapes.
      const match = this.data.sizes.find(s => s.orientation === orientation && !s.out_of_stock)
        || this.data.sizes.find(s => s.orientation === orientation);
      if (match) this.state.size = match;
      this.render();
    }

    setSize(size) {
      // Block selection of out-of-stock sizes: show a toast and leave the
      // existing selection alone. Visual OOS state on the pill handles the rest.
      if (size && size.out_of_stock) {
        this.showToast('This size is currently out of stock.', true);
        return;
      }
      this.state.size = size;
      this.state.isCustomSize = false;
      if (size.orientation) this.state.orientation = size.orientation;
      // reset custom dropdowns
      if (this.els.customW) this.els.customW.value = '';
      if (this.els.customH) this.els.customH.value = '';
      this.render();
    }

    setOption(kind, value, label) {
      // kind: 'edge' | 'frame' | 'filter'
      this.state[kind] = value;
      this.state[kind + 'Label'] = label;
      this.render();
    }

    setService(svc) {
      this.state.service = svc.id;
      this.state.serviceLabel = svc.label || 'None';
      this.state.serviceUpchargeCents = svc.upchargeCents || 0;
      this.state.serviceProductId = svc.productId;
      this.state.serviceVariantId = svc.variantId;
      this.render();
    }

    setHanger(h) {
      this.state.hanger = h.id;
      this.state.hangerLabel = h.label || 'None';
      this.state.hangerUpchargeCents = h.upchargeCents || 0;
      this.state.hangerVariantId = h.variantId || null;
      this.render();
    }

    setFinish(mode, label) {
      this.state.finish = mode;
      this.state.finishLabel = label || mode;
      // Reveal/hide colour picker row when Border Color is selected
      if (this.els.finishColorRow) {
        this.els.finishColorRow.hidden = (mode !== 'border_color');
      }
      this.render();
    }

    applyCustomSize() {
      const w = parseInt(this.els.customW?.value || '', 10);
      const h = parseInt(this.els.customH?.value || '', 10);
      if (!w || !h) {
        this.showToast('Please choose both width and height.', true);
        return;
      }
      const cfg = this.data.customSize || {};
      const minIn = cfg.minIn || 8;
      const maxIn = cfg.maxIn || 60;
      if (w < minIn || w > maxIn || h < minIn || h > maxIn) {
        this.showToast(`Dimensions must be between ${minIn}" and ${maxIn}".`, true);
        return;
      }
      const area = w * h;
      const perSqIn = cfg.pricePerSqInCents || 10;
      const price = Math.round(area * perSqIn);
      const compareMul = cfg.compareMultiplier || 10;
      const compare = Math.round(price * compareMul);
      let orientation = 'square';
      if (w > h * 2.4) orientation = 'panoramic';
      else if (w > h) orientation = 'landscape';
      else if (h > w) orientation = 'portrait';

      const customSize = {
        label: w + '" × ' + h + '" (Custom)',
        width: w,
        height: h,
        orientation: orientation,
        variantId: 0,
        price_cents: price,
        compare_at_cents: compare,
        is_default: false,
        isCustom: true
      };
      this.state.size = customSize;
      this.state.orientation = orientation;
      this.state.isCustomSize = true;
      this.render();
    }

    handleFile(file) {
      // Basic validation
      if (!/^image\//.test(file.type)) {
        this.showToast('Please upload an image file (JPG, PNG, HEIC).', true);
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.showToast('Image is larger than 20 MB. Please upload a smaller file.', true);
        return;
      }
      // free previous
      if (this.state.imageURL) URL.revokeObjectURL(this.state.imageURL);
      this.state.imageFile = file;
      this.state.imageName = file.name;
      this.state.imageURL = URL.createObjectURL(file);
      this.render();
    }

    render() {
      const s = this.state;
      const size = s.size || { label: '', width: 14, height: 14, orientation: 'square' };

      // Title (e.g. "14 x 14 Custom Canvas Print")
      if (this.els.title) {
        const dim = (size.width && size.height) ? size.width + ' x ' + size.height + ' ' : '';
        this.els.title.textContent = dim + this.data.titleSuffix;
      }

      // Price (includes service + hanger upcharges)
      const basePriceCents = size.price_cents != null ? size.price_cents : (this.data.defaultPriceCents || 0);
      const compareCents = size.compare_at_cents != null ? size.compare_at_cents : (this.data.defaultCompareCents || 0);
      const upcharges = (s.serviceUpchargeCents || 0) + (s.hangerUpchargeCents || 0);
      const totalCents = basePriceCents + upcharges;
      const totalCompareCents = compareCents ? compareCents + upcharges : 0;

      if (this.els.priceCompare) {
        if (compareCents && compareCents > basePriceCents) {
          this.els.priceCompare.textContent = formatMoney(compareCents, this.data.moneyFormat);
          this.els.priceCompare.style.display = '';
        } else {
          this.els.priceCompare.style.display = 'none';
        }
      }
      if (this.els.priceSale) this.els.priceSale.textContent = formatMoney(basePriceCents, this.data.moneyFormat);

      if (this.els.priceFinalCompare) {
        if (totalCompareCents && totalCompareCents > totalCents) {
          this.els.priceFinalCompare.textContent = formatMoney(totalCompareCents, this.data.moneyFormat);
          this.els.priceFinalCompare.style.display = '';
        } else {
          this.els.priceFinalCompare.style.display = 'none';
        }
      }
      if (this.els.priceFinal) this.els.priceFinal.textContent = formatMoney(totalCents, this.data.moneyFormat);

      // Panel value chips
      if (this.els.panelValues.product) this.els.panelValues.product.textContent = 'Canvas Print';
      if (this.els.panelValues.shape) this.els.panelValues.shape.textContent = s.orientation.charAt(0).toUpperCase() + s.orientation.slice(1);
      if (this.els.panelValues.sizes) this.els.panelValues.sizes.textContent = (size.dim || size.label) || '';
      if (this.els.panelValues.edges) this.els.panelValues.edges.textContent = s.edgeLabel || this.labelFor('edge', s.edge);
      if (this.els.panelValues.frames) this.els.panelValues.frames.textContent = s.frameLabel || this.labelFor('frame', s.frame);
      if (this.els.panelValues.filters) this.els.panelValues.filters.textContent = s.filterLabel || this.labelFor('filter', s.filter);
      if (this.els.panelValues.services) this.els.panelValues.services.textContent = s.serviceLabel || 'None';
      if (this.els.panelValues.hanger) this.els.panelValues.hanger.textContent = s.hangerLabel || 'None';
      if (this.els.panelValues.finish) this.els.panelValues.finish.textContent = s.finishLabel || 'Mirror Image';

      // Selection states — shape pills
      $$('[data-ccb-orientation]', this.root).forEach(el => {
        el.setAttribute('aria-selected', el.getAttribute('data-ccb-orientation') === s.orientation ? 'true' : 'false');
      });
      // Show only sizes for this orientation; deselect all when custom size is active
      $$('[data-ccb-size]', this.root).forEach(el => {
        const idx = parseInt(el.getAttribute('data-ccb-size'), 10);
        const sz = this.data.sizes[idx];
        const match = sz && sz.orientation === s.orientation;
        el.style.display = match ? '' : 'none';
        const checked = !s.isCustomSize && sz && s.size && sz.label === s.size.label;
        el.setAttribute('aria-checked', checked ? 'true' : 'false');
      });

      // Hanger & Finish aria-checked
      $$('[data-ccb-hanger]', this.root).forEach(el => {
        el.setAttribute('aria-checked', el.getAttribute('data-ccb-hanger') === s.hanger ? 'true' : 'false');
      });
      $$('[data-ccb-finish]', this.root).forEach(el => {
        el.setAttribute('aria-checked', el.getAttribute('data-ccb-finish') === s.finish ? 'true' : 'false');
      });

      // Edges / Frames / Filters aria-checked
      ['edge', 'frame', 'filter'].forEach(kind => {
        $$('[data-ccb-set="' + kind + '"]', this.root).forEach(el => {
          el.setAttribute('aria-checked', el.getAttribute('data-ccb-value') === s[kind] ? 'true' : 'false');
        });
      });

      // Services aria-checked
      $$('[data-ccb-service]', this.root).forEach(el => {
        el.setAttribute('aria-checked', el.getAttribute('data-ccb-service') === s.service ? 'true' : 'false');
      });

      // Preview
      if (this.els.frame) {
        this.els.frame.setAttribute('data-orientation', s.orientation);
        this.els.frame.setAttribute('data-frame', s.frame);
        this.els.frame.setAttribute('data-finish', s.finish);
        this.els.frame.setAttribute('data-edge', s.edge);
        this.els.frame.setAttribute('data-has-image', s.imageURL ? 'true' : 'false');
        // Apply border color (for Border Color finish mode) as a CSS variable
        this.els.frame.style.setProperty('--ccb-finish-color', s.finishColor || '#ffffff');
        // Preview stays at a stable visual size regardless of the selected canvas size.
        // Only the ASPECT RATIO changes so the shape matches the chosen size. Width
        // is handled by CSS per orientation (`.ccb-frame[data-orientation="…"]`).
        if (size.width && size.height) {
          this.els.frame.style.aspectRatio = size.width + ' / ' + size.height;
          this.els.frame.style.removeProperty('width');
          this.els.frame.style.removeProperty('max-width');
          requestAnimationFrame(() => this.updateCubeDimensions && this.updateCubeDimensions());
        }
      }
      if (this.els.canvas) {
        this.els.canvas.setAttribute('data-edge', s.edge);
        if (s.imageURL) this.els.canvas.classList.add('has-image');
        else this.els.canvas.classList.remove('has-image');
      }
      if (this.els.canvasImg) {
        if (s.imageURL) {
          this.els.canvasImg.src = s.imageURL;
          this.els.canvasImg.style.display = '';
        } else {
          this.els.canvasImg.style.display = 'none';
        }
        this.els.canvasImg.style.filter = FILTER_MAP[s.filter] || 'none';
      }
      if (this.els.canvasBlurBg) {
        if (s.imageURL && s.edge === 'blur_wrap') {
          this.els.canvasBlurBg.src = s.imageURL;
          this.els.canvasBlurBg.style.display = '';
        } else {
          this.els.canvasBlurBg.style.display = 'none';
        }
      }
      // Mirror the uploaded image onto the 4 side faces (photo wrap / blur wrap).
      // IMPORTANT: the Liquid template outputs the img with inline `style="display:none"`
      // so the empty-state view doesn't leak a broken-image placeholder; we must clear
      // that inline style here otherwise CSS can't flip it on for photo_wrap edge.
      if (this.els.sideImages && this.els.sideImages.length) {
        this.els.sideImages.forEach(img => {
          if (s.imageURL) {
            img.src = s.imageURL;
            img.style.display = '';
            img.style.filter = FILTER_MAP[s.filter] || 'none';
          } else {
            img.removeAttribute('src');
            img.style.display = 'none';
          }
        });
      }

      // Summary
      this.renderSummary();

      // CTA label
      this.els.cta.forEach(btn => {
        if (btn.getAttribute('data-ccb-state') === 'uploading' || btn.getAttribute('data-ccb-state') === 'adding') return;
        btn.textContent = s.imageURL ? (this.data.addToCartLabel || 'Add to Cart') : (this.data.uploadLabel || 'Upload Image');
      });

    }

    labelFor(kind, value) {
      if (kind === 'edge') {
        const b = (this.data.edges || []).find(x => x.value === value);
        return b ? b.label : value;
      }
      if (kind === 'frame') {
        const b = (this.data.frames || []).find(x => x.value === value);
        return b ? b.label : value;
      }
      if (kind === 'filter') {
        const b = (this.data.filters || []).find(x => x.value === value);
        return b ? b.label : value;
      }
      return value || '';
    }

    renderSummary() {
      if (!this.els.summaryList) return;
      const s = this.state;
      const items = [];
      items.push({ label: 'Custom Canvas Print', value: s.size ? s.size.label : '' });
      // "Side finish" intentionally omitted — client wants Edge only in the summary.
      if (s.edge) items.push({ label: 'Edge', value: this.labelFor('edge', s.edge) });
      if (s.filter && s.filter !== 'none') items.push({ label: 'Filter', value: this.labelFor('filter', s.filter) });
      if (s.hanger && s.hanger !== 'none') items.push({ label: 'Hanger', value: s.hangerLabel });
      if (s.service && s.service !== 'none') items.push({ label: 'Service', value: s.serviceLabel });

      this.els.summaryList.innerHTML = items.map(it => {
        if (it.label === 'Custom Canvas Print') {
          return '<li>' + (it.value ? (it.value + ' ') : '') + it.label + '</li>';
        }
        return '<li>' + it.label + ': ' + it.value + '</li>';
      }).join('');
    }

    handleCTA(btn) {
      if (!this.state.imageURL) {
        this.els.fileInput.click();
        return;
      }
      this.addToCart(btn);
    }

    setCTAState(btn, state) {
      btn.setAttribute('data-ccb-state', state || '');
      if (state === 'uploading') {
        btn.innerHTML = '<span class="ccb-spin"></span><span>Uploading…</span>';
      } else if (state === 'adding') {
        btn.innerHTML = '<span class="ccb-spin"></span><span>Adding to cart…</span>';
      } else {
        btn.textContent = this.state.imageURL ? (this.data.addToCartLabel || 'Add to Cart') : (this.data.uploadLabel || 'Upload Image');
      }
    }

    async addToCart(btn) {
      const s = this.state;
      if (!s.imageFile) {
        this.showToast('Please upload an image first.', true);
        return;
      }
      // Prefer the size-block variant → section setting → fallback (first available product)
      const variantId = (s.size && s.size.variantId && s.size.variantId !== 0)
        ? s.size.variantId
        : this.data.canvasVariantId;
      if (!variantId) {
        this.showToast('Unable to add to cart — no product available. Please contact support.', true);
        return;
      }

      this.els.cta.forEach(b => this.setCTAState(b, 'adding'));

      // Line item properties — Side finish & Frame intentionally omitted
      // (client wants Edge only; Frame option was removed from the UI entirely).
      //
      // The property is named 'Dimensions' (not 'Size') so it does NOT collide
      // with the Shopify variant option also named "Size" ("Taille" in French).
      // Before: cart showed two conflicting lines — "Taille: 18 × 27 po" and
      // "Size: 20 × 27 in". Now the variant option carries the Shopify title
      // under its localized name, and this property carries the shopper's exact
      // selection under a distinct label, so there is no visual duplication.
      const props = {
        'Dimensions': s.size ? s.size.label : '',
        'Orientation': s.orientation.charAt(0).toUpperCase() + s.orientation.slice(1),
        'Edge': this.labelFor('edge', s.edge),
        'Filter': this.labelFor('filter', s.filter),
        'Hanger': s.hangerLabel || 'None',
        'Service': s.serviceLabel || 'None'
      };
      if (s.isCustomSize) props['_Custom size'] = 'yes';
      if (s.notes) props['Adjustment Notes'] = s.notes;

      // Build FormData with file → Shopify uploads to CDN as line-item property URL
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', '1');
      Object.entries(props).forEach(([k, v]) => {
        if (v !== '' && v != null) fd.append('properties[' + k + ']', v);
      });
      fd.append('properties[Custom Image]', s.imageFile, s.imageName);

      try {
        // Add canvas first
        const items = [{ formData: fd }];
        // Service add-on: only queue it if the merchant actually linked a product.
        // (previously, an empty service_product passed through as "0" which Shopify
        // rejected with "cannot find variant", breaking the whole ATC.)
        if (s.service !== 'none' && s.serviceVariantId && s.serviceVariantId !== '0' && s.serviceVariantId !== 0) {
          const svcFd = new FormData();
          svcFd.append('id', s.serviceVariantId);
          svcFd.append('quantity', '1');
          svcFd.append('properties[Applied to]', 'Previous canvas print');
          items.push({ formData: svcFd });
        }
        // Hanger add-on: same guard — only if a real product variant exists.
        if (s.hanger !== 'none' && s.hangerVariantId && s.hangerVariantId !== '0' && s.hangerVariantId !== 0) {
          const hFd = new FormData();
          hFd.append('id', s.hangerVariantId);
          hFd.append('quantity', '1');
          hFd.append('properties[Applied to]', 'Previous canvas print');
          items.push({ formData: hFd });
        }

        // 1) POST each item to /cart/add.js. We send FormData (needed for the uploaded
        //    file). We don't ask for `sections` in this call — Shopify sometimes drops
        //    them when a file is in the payload, which was leaving the drawer blank.
        const base = window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
        for (const it of items) {
          const res = await fetch(base + 'cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json' },
            body: it.formData
          });
          if (!res.ok) {
            let msg = 'Could not add to cart.';
            try { const err = await res.json(); if (err.description) msg = err.description; } catch (_) {}
            throw new Error(msg);
          }
        }

        // 2) Fetch fresh HTML for the Denim cart drawer + cart icon bubble sections,
        //    then hand it to the drawer's `renderContents()` which paints the new
        //    cart contents AND opens the drawer. If that element isn't present
        //    (older/different theme), we fall back to a cart:refresh event.
        const cartDrawerEl = document.querySelector('cart-drawer');
        if (cartDrawerEl && typeof cartDrawerEl.renderContents === 'function') {
          const sectionIds = (
            typeof cartDrawerEl.getSectionsToRender === 'function'
              ? cartDrawerEl.getSectionsToRender()
              : [{id:'cart-drawer'},{id:'cart-icon-bubble'}]
          ).map(s => s.id);
          try {
            const secRes = await fetch(base + '?sections=' + sectionIds.join(','), { headers: { 'Accept': 'application/json' } });
            const sections = await secRes.json();
            cartDrawerEl.renderContents({ sections: sections });
            // Denim's cart-drawer root carries `is-empty` from the initial cart state
            // (set by Liquid when cart was empty on first load). renderContents only
            // strips it from .drawer__inner, so we also strip it here so the newly
            // rendered items are visible instead of the "empty cart" placeholder.
            cartDrawerEl.classList.remove('is-empty');
            cartDrawerEl.querySelectorAll('.is-empty').forEach(el => el.classList.remove('is-empty'));
          } catch (e) {
            // Last-resort: open the drawer even if render failed (user can tap cart icon)
            try { cartDrawerEl.open(); } catch (_) {}
          }
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }

        this.showToast('Added to cart!', false);
      } catch (err) {
        console.error('[CanvasBuilder] Add to cart failed:', err);
        this.showToast(err.message || 'Could not add to cart. Please try again.', true);
      } finally {
        this.els.cta.forEach(b => this.setCTAState(b, null));
      }
    }

    showToast(msg, isError) {
      if (!this.els.toast) return;
      this.els.toast.textContent = msg;
      this.els.toast.classList.toggle('ccb-toast--err', !!isError);
      this.els.toast.classList.add('is-visible');
      clearTimeout(this._toastT);
      this._toastT = setTimeout(() => this.els.toast.classList.remove('is-visible'), 3200);
    }
  }

  function init() {
    document.querySelectorAll('[data-ccb-root]').forEach(root => {
      if (root._ccb) return;
      root._ccb = new CanvasBuilder(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Re-init on section:load (Shopify theme editor)
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
